import { apiGet, apiPost, apiPut, apiDelete, apiPostFormData } from '../services/api';



export interface Offer {
  id: number;
  title: string;
  cost_description: string;
  description: string;
  seats_total: number;
  seats_available?: number;
  scope: string;
  country_id?: number;
  country?: {
    id: number;
    name: string;
  };
  gallery_image_urls?: string[];
  provider_name?: string;
  provider_phone_number?: string;
  fly_tickets_cost?: number;
  accommodation_cost?: number;
  tours_cost?: number;
  others_cost?: string | number;
  status?: 'DRAFT' | 'PUBLISHED';
  created_at: string;
  updated_at: string;
}

export interface GetManyOffersParams {
  page?: number;
  per_page?: number;
  q?: string;
  country_id?: string;
}

export interface GetManyOffersResult {
  data: Offer[];
  total: number;
  page: number;
  per_page: number;
}

export async function getMany(
  params: GetManyOffersParams = {},
): Promise<GetManyOffersResult> {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page) queryParams.append('per_page', params.per_page.toString());
  if (params.q) queryParams.append('q', params.q);
  if (params.country_id) queryParams.append('country_id', params.country_id);

  const response = await apiGet<GetManyOffersResult>(
    `/api/offers?${queryParams.toString()}`,
  );

  return response;
}

export async function getOne(id: number): Promise<Offer> {
  const response = await apiGet<Offer>(`/api/offers/${id}`);
  return response;
}

export async function createOne(data: Partial<Omit<Offer, 'id'>>): Promise<Offer> {
  const response = await apiPost<Offer>('/api/offers', data);
  return response;
}

export async function updateOne(
  id: number,
  data: Partial<Omit<Offer, 'id'>>,
): Promise<Offer> {
  const response = await apiPut<Offer>(`/api/offers/${id}`, data);
  return response;
}

export async function deleteOne(id: number): Promise<void> {
  await apiDelete(`/api/offers/${id}`);
}

export async function uploadGalleryImages(
  offerId: number,
  images: File[],
): Promise<{ media: Array<{ url: string }> }> {
  const formData = new FormData();
  formData.append('resource_type', 'offer');
  formData.append('resource_id', offerId.toString());
  formData.append('collection', 'gallery')
  images.forEach((image) => {
    formData.append('files[]', image);
  });

  const response = await apiPostFormData<{ media: Array<{ url: string }> }>(
    `/api/upload`,
    formData,
  );

  return response;
}

export function validate(data: Partial<Offer>): {
  issues?: Array<{ path?: string[]; message: string }>;
} {
  const issues: Array<{ path?: string[]; message: string }> = [];

  if (!data.title || data.title.trim() === '') {
    issues.push({ path: ['title'], message: 'validation.titleRequired' });
  }

  if (!data.cost_description || data.cost_description.trim() === '') {
    issues.push({ path: ['cost_description'], message: 'validation.costDescriptionRequired' });
  }

  if (!data.description || data.description.trim() === '') {
    issues.push({ path: ['description'], message: 'validation.salesDescriptionRequired' });
  }

  if (data.seats_total === undefined || data.seats_total === null) {
    issues.push({ path: ['seats_total'], message: 'validation.seatsRequired' });
  } else if (data.seats_total < 0) {
    issues.push({ path: ['seats_total'], message: 'validation.seatsGreaterThanOrEqualToZero' });
  }

  // Validate provider phone number if provided
  if (data.provider_phone_number && data.provider_phone_number.trim() !== '') {
    // Validate phone number format (10-15 digits, may include +, spaces, hyphens, parentheses)
    const phoneRegex = /^[\d\+]{10,20}$/;
    const digitsOnly = data.provider_phone_number.replace(/[\s\-+()]/g, '');
    if (!phoneRegex.test(data.provider_phone_number) || digitsOnly.length < 10 || digitsOnly.length > 15) {
      issues.push({ path: ['provider_phone_number'], message: 'validation.invalidPhoneNumber' });
    }
  }

  return issues.length > 0 ? { issues } : {};
}

